import { Box, debounce } from "@mui/material";
import type { AutocompleteRenderGroupParams } from "@mui/material/Autocomplete";
import type { AutocompleteInputChangeReason } from "@mui/material/Autocomplete";
import { useCallback, useEffect, useMemo } from "react";
import { useLazyGetUserDropdownsQuery } from "src/shared/api/dropdowns/user";
import { IAutocompleteOption } from "src/shared/types/field.types";

interface IUserOption {
  value: number;
  name: string;
}

interface IUserGroup {
  grp_name: string;
  users: IUserOption[];
}

export interface IManagerOption extends IAutocompleteOption<number> {
  groupName?: string;
  key?: string;
}

interface IUseManagerSearchParams {
  mainManagerId?: number | "";
}

interface IUseManagerSearchReturn {
  managerOptions: IManagerOption[];
  isFetching: boolean;
  handleManagersOpen: () => void;
  handleManagersInputChange: (
    _: unknown,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
  autocompleteProps: {
    options: IManagerOption[];
    loading: boolean;
    onOpen: () => void;
    onInputChange: (
      _: unknown,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => void;
    groupBy: (option: IManagerOption) => string;
    renderGroup: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  };
}

type TUserDropdownsResponse = Record<string, IUserGroup>;

export const useManagerSearch = ({
  mainManagerId,
}: IUseManagerSearchParams): IUseManagerSearchReturn => {
  const [fetchUsers, { data: userDropdowns, isFetching }] =
    useLazyGetUserDropdownsQuery();

  useEffect(() => {
    if (mainManagerId) {
      fetchUsers();
    }
  }, [mainManagerId, fetchUsers]);

  const loadUsers = useMemo(
    () =>
      debounce((value: string) => {
        const search = value.trim();

        if (!search) {
          fetchUsers();

          return;
        }

        if (search.length >= 3) {
          fetchUsers(search);
        }
      }, 500),
    [fetchUsers],
  );

  useEffect(() => {
    return () => {
      loadUsers.clear();
    };
  }, [loadUsers]);

  const managerOptions = useMemo<IManagerOption[]>(() => {
    const groups = userDropdowns as TUserDropdownsResponse | undefined;

    if (!groups) {
      return [];
    }

    return Object.values(groups).flatMap((group) =>
      group.users.map((user) => ({
        value: user.value,
        name: user.name,
        key: `${group.grp_name || "default"}-${user.value}`,
        groupName: group.grp_name || "",
      })),
    );
  }, [userDropdowns]);

  const handleManagersOpen = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleManagersInputChange = useCallback(
    (_: unknown, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason !== "input") {
        return;
      }

      loadUsers(value);
    },
    [loadUsers],
  );

  const groupBy = useCallback(
    (option: IManagerOption) => option.groupName || "",
    [],
  );

  const renderGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => (
      <li key={params.key}>
        <Box
          sx={{
            px: 2,
            py: 1,
            fontWeight: "bold",
            opacity: 0.8,
            whiteSpace: "normal",
            color: "#919191",
          }}
        >
          {params.group}
        </Box>
        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
      </li>
    ),
    [],
  );

  const autocompleteProps = useMemo(
    () => ({
      options: managerOptions,
      loading: isFetching,
      onOpen: handleManagersOpen,
      onInputChange: handleManagersInputChange,
      groupBy,
      renderGroup,
    }),
    [
      managerOptions,
      isFetching,
      handleManagersOpen,
      handleManagersInputChange,
      groupBy,
      renderGroup,
    ],
  );

  return {
    managerOptions,
    isFetching,
    handleManagersOpen,
    handleManagersInputChange,
    autocompleteProps,
  };
};
